import { J1DownarrowD, J1ToparrowD } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Typography, Theme, Box, Button, Paper, useTheme } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import remarkGfm from 'remark-gfm'
import rehype from 'rehype-raw'
import ReactMarkdown from 'react-markdown'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import { IconButton } from '@e3dc-react/shell/Elements/Buttons/Buttons'
import { dateToHumanTimeWithoutUtcOffset } from '@e3dc-react/shell/Libs/date'

import { NotificationModel, NotificationType } from '../models/notifications.model'
import NotificationTypeSymbol from './NotificationTypeSymbol'
import useNotificationContext from '../hooks/use-notification-context'
import { getReactMarkdownComponents } from '../config'
import { getTransformedMarkdown } from '../transformers/get-transformed-markdown'
import { useApp } from 'src/Hooks'

const useStyles = makeStyles()((theme: Theme) => ({
  accordion: {
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.shades.shade04,
    border: 'solid 1px',
    borderColor: theme.palette.shades.shade03,
    boxShadow: 'none',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    button: {
      padding: 0,
    },
  },
  message: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  markAsReadButtonContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    marginTop: theme.spacing(2),
  },
  markdownContainer: {
    marginTop: theme.spacing(2),
  },
  dateContainer: {
    display: 'flex',
    alignItems: 'center',
    borderRight: `1px solid ${theme.palette.shades.shade03}`,
    paddingRight: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
}))

interface NotificationAccordionProps {
  notification: NotificationModel
  refreshNotifications: () => void
}

/*
 * This component show a notification on the notification page
 * the complete message is shown when the accordion is expanded
 */
const NotificationAccordion: React.FC<NotificationAccordionProps> = ({ notification, refreshNotifications }) => {
  const theme = useTheme()
  const { isMobile } = useApp()
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { markNotificationAsRead } = useNotificationContext()
  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const showMarkAsReadButton = notification.type !== NotificationType.Optional && isOpen && !notification.readAt

  const reactMarkdownComponents = useMemo(() => getReactMarkdownComponents(theme), [theme])

  const markAsRead = useCallback(() => {
    if (notification.readAt !== null) return
    setIsDisabled(true)
    markNotificationAsRead(notification.id)
      .then(refreshNotifications)
      .catch(err => console.error(err))
  }, [markNotificationAsRead, notification.id]) //eslint-disable-line react-hooks/exhaustive-deps

  const openAccordion = (): void => {
    if (notification.type === NotificationType.Optional) {
      markAsRead()
    }

    setIsOpen(!isOpen)
  }

  return (
    <Paper className={classes.accordion}>
      <Box className={classes.content} onClick={openAccordion} style={{ cursor: 'pointer' }}>
        <Box className={classes.message}>
          <Box className={classes.dateContainer}>
            <NotificationTypeSymbol notificationType={notification.type} readAt={notification.readAt} />
            <Typography variant="subtitle2">
              {notification.startAt &&
                dateToHumanTimeWithoutUtcOffset(
                  notification.startAt,
                  isMobile ? t('notifications.start-at-date-mobile') : t('common.format.date-fns-date')
                )}
            </Typography>
          </Box>
          <Typography variant="body2">{notification.title}</Typography>
          <Typography>{notification.type === NotificationType.Survey ? <LinkButton content={t('notifications.surveyLink')} /> : null}</Typography>
        </Box>
        <IconButton onClick={openAccordion} icon={isOpen ? <J1ToparrowD /> : <J1DownarrowD />} />
      </Box>
      {isOpen && (
        <Box className={classes.markdownContainer}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehype]} components={reactMarkdownComponents}>
            {getTransformedMarkdown(notification.text)}
          </ReactMarkdown>
        </Box>
      )}
      {showMarkAsReadButton && (
        <Box className={classes.markAsReadButtonContainer}>
          <Button onClick={markAsRead} disabled={isDisabled}>
            {t('notification.markAsReadButton')}
          </Button>
        </Box>
      )}
    </Paper>
  )
}

export default NotificationAccordion
